import { Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { Editor } from '@tiptap/react';
import cn from 'classnames';
import { useLayoutEffect, useRef } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import type { AiChatMessage } from 'src/@types/ai';
import { UserType } from 'src/@types/workspace-info';
import { useAuthContext } from 'src/auth/useAuthContext';
import { MotionContainer } from 'src/components/animate';
import { AIInputStateControl } from 'src/features/AIInput';
import { useSelector } from 'src/redux/store';
import { transition } from '../../../shared/constants';
import { StyledMessageDiv, StyledWrapper } from '../../../shared/styles';
import { AIMessageContent } from './AIMessageContent';
import { AIMessageAudioPlayer } from './AIMessageContent/AIMessageAudioPlayer';
import { BotMessageLoading } from './AIMessageContent/BotMessageLoading';

const List = styled('div')(({ theme }) => ({
  '& > *:last-child > *': {
    paddingBottom: `${theme.spacing(5)}`,
  },
}));

type ChatMessagesProps = {
  editor: Editor;
  store: AIInputStateControl;
  messages: any;
};

export const AIMessages = ({ editor, store, messages }: ChatMessagesProps) => {
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  const previousTotalMessages = useRef<number>(messages.length);
  const totalMessages = messages.length;

  const { user } = useAuthContext();
  const coperaUser = useSelector((state) =>
    state.workspace.workspace.users.find((u) => u.type === UserType.AI),
  );

  useLayoutEffect(() => {
    if (previousTotalMessages.current !== totalMessages) {
      virtuosoRef.current?.scrollToIndex({
        index: 'LAST',
        align: 'end',
      });
    }
  }, [totalMessages]);

  const itemContent = (_index: number, message: AiChatMessage) => {
    const sender = message.role === 'user' ? user! : coperaUser;

    return (
      <MotionContainer key={message._id}>
        <StyledMessageDiv
          variants={transition}
          className={cn({ userMessage: message.role === 'user' })}
        >
          <StyledWrapper
            className={cn('message-wrapper', {
              loading: message.status === 'loading' && !message.content,
            })}
          >
            <Stack className="response-wrapper">
              {message.status === 'loading' && !message.content ? (
                <BotMessageLoading />
              ) : (
                <>
                  <AIMessageContent
                    message={message}
                    sender={sender}
                    editor={editor}
                  />
                  {message.role === 'assistant' && message.content && (
                    <AIMessageAudioPlayer message={message} store={store} />
                  )}
                </>
              )}
            </Stack>
          </StyledWrapper>
        </StyledMessageDiv>
      </MotionContainer>
    );
  };

  return (
    <Virtuoso
      ref={virtuosoRef}
      itemContent={itemContent}
      firstItemIndex={messages.length ? messages.length - 1 : 0}
      data={messages}
      followOutput="auto"
      increaseViewportBy={900}
      defaultItemHeight={400}
      components={{ List }}
      computeItemKey={(_, item) => item._id}
    />
  );
};
